import React from 'react';

import ContextSingleton from '../__singletons__/context-singleton';
import { ALERT_SEVERITY_ENUM, RESOURCES_TYPES_ENUM } from '../enums';
import * as helpers from '../helpers';
import * as types from '../types';

type IUseUpdateUserPreferences<TResourcePreference = types.common.ResourcePreference> = {
  resourceId: number;
  resourceType: RESOURCES_TYPES_ENUM;
  resource?: { shared?: boolean };
  setAlert?: (_alert: types.common.Alert | null) => void;
  onSuccess?: (_resourcePreference: TResourcePreference) => void;
};

/**
 * Hook that provides methods to load and update resource preferences
 *
 * @template TResource Type of the resource being updated
 * @template TResourcePreference Type of the resource preference
 * @param resourceId - ID of the resource to update
 * @param resourceType - Type of the resource (e.g., 'universes', 'signals')
 * @param resource - Optional resource object to check if shared
 * @param setAlert - Optional function to set alert messages for errors
 * @param onSuccess - Optional callback executed on successful update
 * @returns Object with load and update methods
 *
 */
const useUpdateUserPreferences = <TResourcePreference = types.common.ResourcePreference>({
  resourceId,
  resourceType,
  setAlert,
  onSuccess,
}: IUseUpdateUserPreferences<TResourcePreference>) => {
  const api = React.useMemo(() => ContextSingleton.getInstance().api, []);
  const preferenceIdRef = React.useRef<number | null>(null);

  const load = React.useCallback(async () => {
    try {
      const resp = await api.resource_preferences.search({
        query: ['$and', ['item_id_ref', '=', String(resourceId)], ['item_type', '=', resourceType]],
      });

      let resourcePreference = resp.data?.[0] as TResourcePreference;

      if (!resourcePreference) {
        const createResp = await api.resource_preferences.create({
          item_id_ref: String(resourceId),
          item_type: resourceType,
          preferences: {},
        });
        resourcePreference = createResp.data as TResourcePreference;
      }

      preferenceIdRef.current = (resourcePreference as any).id;
      if (onSuccess) onSuccess(resourcePreference);
    } catch (error) {
      const errorMessage = `Error loading resource preference - ${helpers.parseApiError(
        error as types.common.ApiError
      )}`;
      // We don't want to set an alert here because we want to handle this error on the caller
      throw new Error(errorMessage);
    }
  }, [resourceId, resourceType, setAlert, onSuccess]);

  const update = React.useCallback(
    async (payload: Partial<TResourcePreference>) => {
      if (preferenceIdRef.current === null) {
        throw new Error('Resource preference must be loaded before updating');
      }
      try {
        const resp = await api.resource_preferences.update(preferenceIdRef.current, payload);
        const updatedPreference = resp.data as TResourcePreference;

        if (onSuccess) onSuccess(updatedPreference);
      } catch (error) {
        const errorMessage = 'Error updating preferences';
        if (setAlert) setAlert({ severity: ALERT_SEVERITY_ENUM.ERROR, message: errorMessage });
        throw new Error(errorMessage);
      }
    },
    [setAlert, onSuccess]
  );

  return { load, update };
};

export default useUpdateUserPreferences;
